import type { FC } from 'react';
import type { Maps, Coords } from 'google-map-react';
import GoogleMap from 'google-map-react';
import type { LocationMapMarkerProps } from './LocationMapMarker';
import LocationMapMarker from './LocationMapMarker';
import { getArchiProMapVariables, GOOGLE_MAP_STYLES } from './LocationConstant';
import { useLoaderData } from 'react-router';
import type {
    LocationDataType,
    ContactLoaderData,
} from '@modules/professional/type/contact-page-types';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { useIsSafari } from '@modules/root/hook';
import { useProfessionalInitialRouteData } from '~/modules/professional/hook/use-professional-initial-route-data';

interface LocationMapProps {
    zoom: number;
    center: Coords;
    filteredLocations: LocationDataType[];
    hoveredLocation?: LocationDataType;
    clickedLocation?: LocationDataType;
    onMapMarkerHover: (location: LocationDataType) => void;
    onMapMarkerClick: (location: LocationDataType, key?: number) => void;
    onMapPopupClose?: () => void;
    linkedProfiles: ContactLoaderData['linkedProfiles'];
}
export const LocationMap: FC<LocationMapProps> = ({
    zoom,
    center,
    filteredLocations,
    hoveredLocation,
    clickedLocation,
    onMapMarkerHover,
    onMapMarkerClick,
    onMapPopupClose,
    linkedProfiles,
}) => {
    const { frontendEnv } = useLoaderData<ContactLoaderData>();
    const { mobile: isMobile } = useAppDisplayModeContext();
    const isSafari = useIsSafari();

    const professional = useProfessionalInitialRouteData();
    const professionalCountry = professional.Country || frontendEnv.country;
    const mapVariables = getArchiProMapVariables(professionalCountry, isMobile);

    const onChildClick = (key: number, childProps: LocationMapMarkerProps) => {
        const location = filteredLocations.find(
            (m) => m.ID === childProps.locationID,
        );
        if (location) {
            onMapMarkerClick(location, key);
        }
    };

    const onChildMouseEnter = (
        key: number,
        childProps: LocationMapMarkerProps,
    ) => {
        const location = filteredLocations.find(
            (m) => m.ID === childProps.locationID,
        );
        if (hoveredLocation?.ID !== childProps.locationID && location) {
            if (isMobile && isSafari) {
                onMapMarkerClick(location, key);
            } else {
                onMapMarkerHover(location);
            }
        }
    };

    const mapOptions = (maps: Maps) => {
        return {
            styles: GOOGLE_MAP_STYLES,
            fullscreenControl: false,
            zoomControl: true,
            zoomControlOptions: {
                position: maps.ControlPosition.TOP_RIGHT,
            },
            minZoom: mapVariables.zoom.min,
            maxZoom: mapVariables.zoom.max,
            restriction: mapVariables.restriction,
            clickableIcons: false,
        };
    };
    return (
        <GoogleMap
            bootstrapURLKeys={{
                key: frontendEnv.CLIENT_GOOGLE_MAP_KEY,
                language: 'en',
                region: 'new zealand',
            }}
            center={center}
            zoom={zoom}
            options={mapOptions}
            onChildClick={onChildClick}
            onChildMouseEnter={onChildMouseEnter}
        >
            {filteredLocations.map(
                (locationData, index) =>
                    locationData?.ID &&
                    locationData.MapCoordinates && (
                        <LocationMapMarker
                            key={index}
                            lat={locationData.MapCoordinates.Latitude}
                            lng={locationData.MapCoordinates.Longitude}
                            locationID={locationData.ID}
                            hoveredLocation={hoveredLocation}
                            clickedLocation={clickedLocation}
                            onMapPopupClose={onMapPopupClose}
                            linkedProfiles={linkedProfiles}
                        />
                    ),
            )}
        </GoogleMap>
    );
};
